/**
 * The Desk — portfolio position panel (Reppr Elite redesign, batch 3).
 *
 * Real data only. Figures with no underlying records render an em dash rather
 * than an estimate; see `utils/deskMetrics.ts` for which and why.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { listContracts } from '../../api/contracts'
import { listDeals } from '../../api/deals'
import { listClients } from '../../api/clients'
import { listCommissionLedger } from '../../api/commissions'
import { listInvoices } from '../../api/invoices'
import { feesEarned, yearToDate } from '../../utils/feeIncome'
import { useSession } from '../../hooks/useSession'
import { useWorkView } from '../../hooks/useWorkView'
import { buildAthleteScope } from '../../utils/athleteScope'
import type { Client } from '../../types'
import { queryKeys } from '../../utils/queryKeys'
import { parseDateOnly, startOfLocalDay } from '../../utils/formatters'
import { isInvoiceOwed, invoiceOpenBalance } from '../../utils/invoiceBalance'
import { computeDeskMetrics, fmtMoney, fmtPct } from '../../utils/deskMetrics'
import CashWaterfall from './CashWaterfall'
import styles from './TheDesk.module.css'

function Kpi({
  label, value, footLeft, footRight, muted, tone, secondary,
}: {
  label: string
  value: string
  footLeft?: string
  footRight?: string
  muted?: boolean
  tone?: 'accent' | 'positive'
  /** Hidden under 768px — the handoff's mobile stat grid is 2x2, not six cells. */
  secondary?: boolean
}) {
  return (
    <div className={`${styles.kpi} ${secondary ? styles.kpiSecondary : ''}`}>
      <div className={styles.kpiLabel}>{label}</div>
      <div className={`${styles.kpiValue} ${muted ? styles.kpiValueMuted : ''}`}>{value}</div>
      {(footLeft || footRight) && (
        <div className={styles.kpiFoot}>
          {footLeft && (
            <span className={tone === 'accent' ? styles.kpiAccent : tone === 'positive' ? styles.kpiPositive : undefined}>
              {footLeft}
            </span>
          )}
          {footRight && <span>{footRight}</span>}
        </div>
      )}
    </div>
  )
}

export default function TheDesk() {
  const { user } = useSession()
  const hasAgency = !!(user as any)?.agency_id

  const contractsQ = useQuery({ queryKey: queryKeys.contracts.list(), queryFn: () => listContracts(), enabled: hasAgency })
  const dealsQ = useQuery({ queryKey: queryKeys.deals.list(), queryFn: () => listDeals(), enabled: hasAgency })
  const clientsQ = useQuery({ queryKey: queryKeys.clients.list(), queryFn: () => listClients(), enabled: hasAgency })
  const ledgerQ = useQuery({ queryKey: queryKeys.commissionLedger.list(), queryFn: () => listCommissionLedger(), enabled: hasAgency })
  const invoicesQ = useQuery({ queryKey: queryKeys.invoices.list(), queryFn: () => listInvoices(), enabled: hasAgency })

  const loading = contractsQ.isLoading || dealsQ.isLoading || clientsQ.isLoading
  const error = contractsQ.error || dealsQ.error || clientsQ.error

  // Mine or Everyone, by athlete ownership (utils/athleteScope). The desk
  // reads whole collections and narrows here, so the figures on an agent's
  // desk are their book, not the agency's.
  const { viewMode, userId } = useWorkView()
  const m = useMemo(() => {
    const clients = (clientsQ.data ?? []) as Client[]
    const scope = buildAthleteScope({ userId, mode: viewMode, clients })
    const myClients = viewMode === 'my-work' ? clients.filter(c => scope.myClientIds.has(c.id)) : clients
    // Fees earned this year: the ledger's commission_earned rows, narrowed
    // to the agent's own rows on a Mine desk.
    const ledgerRows = (ledgerQ.data ?? []).filter(e => viewMode !== 'my-work' || !userId || e.agent_id === userId)
    const fees = feesEarned(ledgerRows, yearToDate())
    return computeDeskMetrics(
      (contractsQ.data ?? []).filter(c => scope.includesRow(c as any)),
      (dealsQ.data ?? []).filter(d => scope.includesDeal(d as any)),
      myClients,
      fees.gross,
    )
  // ledgerQ.data belongs here: the fees figure below is computed from it.
  // Without it the memo kept whatever the ledger happened to hold on the
  // render that last changed one of the others — on a cold load where the
  // ledger answers last, that is an empty list, and the desk then reported
  // no fees earned all year and never corrected itself.
  }, [contractsQ.data, dealsQ.data, clientsQ.data, ledgerQ.data, viewMode, userId])

  /**
   * Cash next 90 days: open invoice balances falling due inside the window.
   *
   * This cell used to be the literal string "NO INVOICES" beside an em dash,
   * printed whether or not any invoice existed. It was wrong on this very
   * agency, which has four - three of them outstanding and every one already
   * past due. A figure nobody computes is not a figure.
   *
   * Money already overdue is deliberately not counted as cash arriving in the
   * next ninety days; it was due and it has not come. It is named underneath
   * instead, because a desk that stays silent about $658K of late money is
   * worse than one that shows a dash.
   */
  const cash90 = useMemo(() => {
    // Same athlete scoping the rest of the desk uses: an invoice belongs to
    // whoever owns the athlete it is raised against.
    const scope = buildAthleteScope({ userId, mode: viewMode, clients: (clientsQ.data ?? []) as Client[] })
    const rows = ((invoicesQ.data ?? []) as any[]).filter(i => scope.includesRow(i))
    const today = startOfLocalDay(new Date()).getTime()
    const horizon = today + 90 * 86_400_000
    let due: number | null = null
    let dueCount = 0
    let overdue: number | null = null
    let overdueCount = 0

    for (const i of rows) {
      if (!isInvoiceOwed(i)) continue
      const open = invoiceOpenBalance(i)

      const when = parseDateOnly(i.due_date)
      if (!when) continue
      const t = startOfLocalDay(when).getTime()
      if (t < today) { overdue = (overdue ?? 0) + open; overdueCount++ }
      else if (t <= horizon) { due = (due ?? 0) + open; dueCount++ }
    }
    return { due, dueCount, overdue, overdueCount, anyInvoices: rows.length > 0 }
  }, [invoicesQ.data, clientsQ.data, viewMode, userId])

  const stamp = new Date()
    .toLocaleString('en-US', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
    .toUpperCase()

  if (error) {
    return (
      <div className={styles.desk}>
        <div className={styles.panel}>
          <div className={styles.empty}>
            Couldn’t load portfolio position — {(error as Error).message}
          </div>
        </div>
      </div>
    )
  }

  const concentrated = (m.concentration.topThreePct ?? 0) >= 50

  return (
    <div className={styles.desk}>
      {/* Screen title + subtitle live in the app shell header (AppHeader).
          The design shows the Desk body starting straight at the stat strip. */}

      <div className={styles.kpis}>
        <Kpi label="Portfolio AUM" value={loading ? '—' : fmtMoney(m.aum)} footLeft={`${contractsQ.data?.length ?? 0}`} footRight="CONTRACTS" />
        <Kpi label="Guaranteed" value={loading ? '—' : fmtMoney(m.guaranteed)} footLeft={fmtPct(m.guaranteedPct)} footRight="OF AUM" tone="positive" />
        <Kpi label="Open pipeline" value={loading ? '—' : fmtMoney(m.openPipeline)} footLeft={`${m.openCount}`} footRight="STRUCTURES" />
        <Kpi label="Weighted" value={loading ? '—' : fmtMoney(m.weightedPipeline)} footLeft={fmtPct(m.weightedPct)} footRight="OF GROSS" tone="accent" />
        {/* The ledger is its own query, so it carries its own states. It is
            not folded into the page-wide `loading`/`error`: a ledger that is
            slow or down should not blank out AUM and pipeline, and a figure
            that is merely unread must never be reported as a figure of zero. */}
        <Kpi
          secondary
          label="Fees booked YTD"
          value={ledgerQ.isLoading || ledgerQ.error ? '—' : fmtMoney(m.feesYtd)}
          muted={ledgerQ.isLoading || !!ledgerQ.error || m.feesYtd == null}
          footRight={
            ledgerQ.isLoading
              ? 'READING LEDGER'
              : ledgerQ.error
                ? 'LEDGER UNAVAILABLE'
                : m.feesYtd == null
                  ? 'NO LEDGER ROWS THIS YEAR'
                  : 'COMMISSION LEDGER'
          }
        />
        <Kpi
          secondary
          label="Cash next 90d"
          value={invoicesQ.isLoading || invoicesQ.error ? '—' : fmtMoney(cash90.due)}
          muted={invoicesQ.isLoading || !!invoicesQ.error || cash90.due == null}
          footRight={
            invoicesQ.isLoading
              ? 'READING INVOICES'
              : invoicesQ.error
                ? 'INVOICES UNAVAILABLE'
                : cash90.overdueCount > 0 && cash90.due == null
                  ? `${cash90.overdueCount} OVERDUE · ${fmtMoney(cash90.overdue)}`
                  : cash90.dueCount > 0
                    ? `${cash90.dueCount} INVOICE${cash90.dueCount === 1 ? '' : 'S'} DUE`
                    : cash90.anyInvoices
                      ? 'NOTHING DUE IN 90 DAYS'
                      : 'NO INVOICES'
          }
        />
      </div>

      <div className={styles.grid}>
        {/* Pipeline by stage */}
        <div className={styles.panel}>
          <div className={styles.panelHead}>
            <h2 className={styles.panelTitle}>Pipeline by stage</h2>
            <span className={styles.panelMeta}>
              {m.openCount} open · {fmtMoney(m.openPipeline)}
            </span>
          </div>
          <div className={styles.tableWrap}>
            {m.stages.length === 0 ? (
              <div className={styles.empty}>{loading ? 'Loading…' : 'No deals yet.'}</div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th style={{ textAlign: 'right' }}>N</th>
                    <th style={{ textAlign: 'right' }}>Gross</th>
                    <th style={{ textAlign: 'right' }}>Prob</th>
                    <th style={{ textAlign: 'right' }}>Weighted</th>
                  </tr>
                </thead>
                <tbody>
                  {m.stages.map(s => (
                    <tr key={s.stage}>
                      <td className={styles.stageName}>{s.stage}</td>
                      <td className={styles.num}>{s.count}</td>
                      <td className={styles.num}>{fmtMoney(s.gross)}</td>
                      <td className={styles.num}>{fmtPct(s.probability)}</td>
                      <td className={styles.num}>{fmtMoney(s.weighted)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Concentration */}
        <div className={styles.panel}>
          <div className={styles.panelHead}>
            <h2 className={styles.panelTitle}>Concentration</h2>
            {!loading && m.holdings.length > 0 && (
              <span className={concentrated ? styles.flag : styles.panelMeta}>
                {concentrated ? 'Elevated' : 'Normal'}
              </span>
            )}
          </div>
          {m.holdings.length === 0 ? (
            <div className={styles.empty}>{loading ? 'Loading…' : 'No contracts yet.'}</div>
          ) : (
            <>
              <div className={styles.row}>
                <span className={styles.rowLabel}>Top 3 clients</span>
                <span className={styles.rowValue}>{fmtPct(m.concentration.topThreePct)} of AUM</span>
              </div>
              <div className={styles.row}>
                <span className={styles.rowLabel}>Single league exposure</span>
                <span className={styles.rowValue}>
                  {m.concentration.topLeague
                    ? `${fmtPct(m.concentration.topLeague.pct)} ${m.concentration.topLeague.league}`
                    : '—'}
                </span>
              </div>
              <div className={styles.row}>
                <span className={styles.rowLabel}>Wtd. avg. years remaining</span>
                <span className={styles.rowValue}>
                  {m.concentration.weightedAvgYearsRemaining != null
                    ? m.concentration.weightedAvgYearsRemaining.toFixed(1)
                    : '—'}
                </span>
              </div>
              <div className={styles.row}>
                <span className={styles.rowLabel}>Expiring within 12 months</span>
                <span className={styles.rowValue}>{fmtMoney(m.concentration.expiringWithin12Months)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Contract cash waterfall */}
      <CashWaterfall contracts={(contractsQ.data ?? []) as any} loading={loading} />

      {/* Top holdings */}
      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <h2 className={styles.panelTitle}>The Book</h2>
          <span className={styles.panelMeta}>Top holdings by contract value</span>
        </div>
        {m.holdings.length === 0 ? (
          <div className={styles.empty}>{loading ? 'Loading…' : 'No contracts yet.'}</div>
        ) : (
          m.holdings.slice(0, 6).map((h, i) => (
            <div className={styles.holding} key={h.clientId}>
              <span className={styles.rank}>{String(i + 1).padStart(2, '0')}</span>
              <div style={{ minWidth: 0 }}>
                <Link to={`/clients/${h.clientId}`} className={styles.holdingName}>{h.name}</Link>
                <div className={styles.holdingThru}>{h.through ? `Thru ${h.through}` : 'No end date'}</div>
              </div>
              <div className={styles.holdingRight}>
                <div className={styles.holdingShare}>{fmtPct(h.share)}</div>
                <div className={styles.holdingValue}>{fmtMoney(h.value)}</div>
              </div>
              <div className={styles.bar}>
                <div className={styles.barFill} style={{ width: `${Math.min(100, h.share)}%` }} />
              </div>
            </div>
          ))
        )}
        <div className={styles.note}>
          Ranked by total contract value. Fees are not broken out per holding — see Fees booked YTD
          above, and the fee line on the cash waterfall.
        </div>
      </div>
    </div>
  )
}
